import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Plus, Search, Filter, Edit, Trash2, Eye, Wallet, ChevronLeft, ChevronRight } from 'lucide-react';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import { PERSIAN_TEXTS } from '../../constants/persianTexts';
import playerService from '../../services/playerService';
import ChargeWalletModal from '../../components/Players/ChargeWalletModal';
import toast from 'react-hot-toast';

const PlayersContainer = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 20px;
  @media (max-width: 768px) {
    gap: 12px;
    margin-bottom: 18px;
  }
`;

const Title = styled.h1`
  color: #ffffff;
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  background: linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
  flex-wrap: wrap;
  @media (max-width: 768px) {
    gap: 10px;
    width: 100%;
    & > * {
      flex: 1 1 100%;
    }
  }
`;

const PlayersTable = styled.div`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 12px;
  overflow: hidden;
  @media (max-width: 768px) {
    border-radius: 10px;
  }
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr;
  gap: 20px;
  padding: 20px;
  background: linear-gradient(90deg, #2d2d44 0%, #1a1a2e 100%);
  border-bottom: 1px solid #2d2d44;
  font-weight: 600;
  color: #ffffff;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const TableRow = styled(motion.div)`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr;
  gap: 20px;
  padding: 20px;
  border-bottom: 1px solid #2d2d44;
  align-items: center;
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(90deg, rgba(0, 212, 255, 0.05) 0%, transparent 100%);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 14px;
  }
`;

const PlayerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const PlayerAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 16px;
`;

const PlayerDetails = styled.div`
  flex: 1;
`;

const PlayerName = styled.div`
  color: #ffffff;
  font-weight: 600;
  margin-bottom: 5px;
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const PlayerEmail = styled.div`
  color: #6c6c8a;
  font-size: 14px;
  word-break: break-word;
  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const StatusBadge = styled.div`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => props.active ? 'linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)' : 'linear-gradient(135deg, #ff6b6b 0%, #ff8e8e 100%)'};
  color: white;
`;

const WalletAmount = styled.div`
  color: #4ecdc4;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 5px;
  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  flex-wrap: wrap;
  @media (max-width: 768px) {
    justify-content: space-between;
    gap: 8px;
  }
`;

const ActionButton = styled.button`
  background: none;
  border: 1px solid #2d2d44;
  border-radius: 6px;
  padding: 8px;
  color: #b8b8d1;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #2d2d44;
    color: #00d4ff;
    border-color: #00d4ff;
  }
  @media (max-width: 768px) {
    flex: 1 1 calc(33% - 8px);
    min-width: 44px;
    padding: 10px;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 30px;
  flex-wrap: wrap;
`;

const PageButton = styled.button`
  background: ${props => props.active ? 'linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%)' : 'transparent'};
  border: 1px solid #2d2d44;
  border-radius: 6px;
  padding: 8px 12px;
  color: ${props => props.active ? 'white' : '#b8b8d1'};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%);
    color: white;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6c6c8a;
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 20px;
`;

const EmptyTitle = styled.h3`
  color: #b8b8d1;
  font-size: 20px;
  margin: 0 0 10px 0;
`;

const EmptyDescription = styled.p`
  color: #6c6c8a;
  font-size: 16px;
  margin: 0;
`;

const Players = () => {
  const [playersData, setPlayersData] = useState({
    entities: [],
    page: 1,
    pageCount: 1,
    totalItems: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [showChargeModal, setShowChargeModal] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  useEffect(() => {
    fetchPlayers();
  }, [currentPage, searchTerm, pageSize]);

  const fetchPlayers = async () => {
    try {
      setLoading(true);
      const response = await playerService.getPlayers({
        Page: currentPage,
        TakeEntity: pageSize,
        SearchTerm: searchTerm
      });
      
      // Handle different response formats like old website
      if (response && (response.entities || response.items)) {
        setPlayersData({
          entities: response.entities || response.items || [],
          page: response.page || 1,
          pageCount: response.pageCount || 1,
          totalItems: response.totalItems || 0
        });
      } else {
        // Fallback for array response
        setPlayersData({
          entities: Array.isArray(response) ? response : [],
          page: 1,
          pageCount: 1,
          totalItems: Array.isArray(response) ? response.length : 0
        });
      }
    } catch (error) {
      toast.error('خطا در بارگذاری بازیکنان');
      console.error('Error fetching players:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditPlayer = (player) => {
    // TODO: Implement edit player modal
    toast.success(`ویرایش بازیکن: ${player.username}`);
  };

  const handleDeletePlayer = (player) => {
    if (window.confirm('آیا از حذف این بازیکن اطمینان دارید؟')) {
      // TODO: Implement delete player
      toast.success(`حذف بازیکن: ${player.username}`);
    }
  };

  const handleChargeWallet = (player) => {
    setSelectedPlayer(player);
    setShowChargeModal(true);
  };

  const handleChargeSuccess = () => {
    fetchPlayers(); // Refresh to show updated balance
  };

  const handleCloseChargeModal = () => {
    setShowChargeModal(false);
    setSelectedPlayer(null);
  };

  if (loading) {
    return (
      <PlayersContainer>
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <div style={{ color: '#b8b8d1' }}>در حال بارگذاری بازیکنان...</div>
        </div>
      </PlayersContainer>
    );
  }

  return (
    <PlayersContainer>
      <Header>
        <Title>مدیریت بازیکنان</Title>
        <SearchContainer>
          <Input
            placeholder="جستجو بر اساس نام کاربری، ایمیل یا نام نمایشی..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ minWidth: '300px' }}
          />
          <select
            value={pageSize}
            onChange={(e) => setPageSize(parseInt(e.target.value))}
            style={{
              background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
              border: '1px solid #2d2d44',
              borderRadius: '8px',
              padding: '8px 12px',
              color: '#ffffff',
              fontSize: '14px'
            }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <Button variant="outline" icon={<Filter size={16} />}>
            فیلتر
          </Button>
          <div style={{ color: '#6c6c8a', fontSize: '14px', marginRight: '10px' }}>
            مجموع: {playersData.totalItems} بازیکن
          </div>
        </SearchContainer>
      </Header>

      {playersData.entities.length === 0 ? (
        <EmptyState>
          <EmptyIcon>👥</EmptyIcon>
          <EmptyTitle>هیچ بازیکنی یافت نشد</EmptyTitle>
          <EmptyDescription>
            {searchTerm ? 'نتیجه‌ای برای جستجوی شما یافت نشد' : 'هنوز بازیکنی ثبت نام نکرده است'}
          </EmptyDescription>
        </EmptyState>
      ) : (
        <PlayersTable>
          <TableHeader>
            <div>نام کاربری</div>
            <div>نام</div>
            <div>اعتبار</div>
            <div>امتیاز</div>
            <div>رنک</div>
            <div>عملیات</div>
          </TableHeader>
          
          {playersData.entities.map((player, index) => (
            <TableRow
              key={player.id || index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <div>
                <strong>{player.username || 'نام کاربری ندارد'}</strong>
              </div>
              
              <div>
                <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={player.fullName}>
                  {player.fullName || '-'}
                </div>
              </div>
              
              <div>{player.balance || 0}</div>
              
              <div>{player.score || 0}</div>
              
              <div>{player.rankName || '-'}</div>
              
              <Actions>
                <ActionButton onClick={() => handleChargeWallet(player)} title="شارژ کیف پول">
                  <Wallet size={16} />
                </ActionButton>
                <ActionButton onClick={() => handleEditPlayer(player)} title="ویرایش">
                  <Edit size={16} />
                </ActionButton>
                <ActionButton onClick={() => handleDeletePlayer(player)} title="حذف">
                  <Trash2 size={16} />
                </ActionButton>
              </Actions>
            </TableRow>
          ))}
        </PlayersTable>
      )}

      {playersData.pageCount > 1 && (
        <Pagination>
          <PageButton
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          >
            قبلی
          </PageButton>
          
          {Array.from({ length: playersData.pageCount }, (_, i) => i + 1).map(page => (
            <PageButton
              key={page}
              active={page === currentPage}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </PageButton>
          ))}
          
          <PageButton
            onClick={() => setCurrentPage(Math.min(playersData.pageCount, currentPage + 1))}
            disabled={currentPage === playersData.pageCount}
          >
            بعدی
          </PageButton>
        </Pagination>
      )}

      {/* Charge Wallet Modal */}
      <ChargeWalletModal
        show={showChargeModal}
        player={selectedPlayer}
        onClose={handleCloseChargeModal}
        onSuccess={handleChargeSuccess}
      />
    </PlayersContainer>
  );
};

export default Players;
